/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import { Dispatch } from 'react';
import { loadUniversalTrackerModal } from '../../../../../actions/universalTrackerModal';
import { UpdateActions } from '../../../../../actions/universalTrackerValue';
import { UniversalTrackerValuePlain } from '../../../../../types/surveyScope';
import { AssuranceButton } from '../../../../assurance/assurance-button';
import DelegationModal from '../../../../delegation-modal';
import CompositeValueHistory from '../../../../value-history/CompositeValueHistory';
import { SubmitVerifiedButtonState, QuestionButtonTooltip } from '../../constants';
import { QuestionToolbarProps } from '../QuestionToolbar';
import { ToolbarButtonProps } from '../ToolbarButton';
import { UniversalTrackerType } from '../../../../../model/UniversalTracker';
import { QUESTION } from '../../../../../constants/terminology';

interface ActionButtonProps extends QuestionToolbarProps {
  dispatch: Dispatch<any>;
  isStakeholder: boolean;
}

export function getActionButtons(props: ActionButtonProps): ToolbarButtonProps[] {
  const { utrv, survey, isStakeholder, isAggregate, handleToggleComments, handleRefreshQuestion, isCompleted, dispatch } = props;

  const isReadOnly = Boolean(survey.completedDate);

  const items: ToolbarButtonProps[] = [];
  items.push({
    contents: <i className='fas fa-user-plus' />,
    tooltip: `View/Edit this ${QUESTION.SINGULAR}'s contributors and verifiers`,
    disabled: isCompleted || !isStakeholder || isAggregate,
    modal: {
      component: DelegationModal,
      utrvs: [utrv],
      handleSubmit: handleRefreshQuestion,
      survey: props.survey,
      initiativeId: props.survey.initiativeId,
    }
  });
  items.push({
    contents: <i className='fas fa-history' />,
    tooltip: `View this ${QUESTION.SINGULAR}'s data history`,
    disabled: isAggregate,
    onClick: () => dispatch(
      loadUniversalTrackerModal(
        {
          universalTrackerId: utrv.universalTrackerId,
          universalTrackerType: UniversalTrackerType.Utr,
          actionValueId: utrv._id,
          params: { surveyId: survey._id }
        }
      ))
  });
  items.push({
    contents: <i className='fas fa-clipboard' />,
    tooltip: `View this ${QUESTION.SINGULAR}'s data provenance`,
    disabled: isAggregate,
    modal: {
      component: CompositeValueHistory,
      isReadOnly: isReadOnly,
      isModal: true,
      ids: [utrv._id]
    }
  });

  const { text, component, onClick } = AssuranceButton(props)
  if (component) {
    items.push({
      contents: component,
      disabled: !isStakeholder || isAggregate,
      tooltip: text,
      onClick,
    });
  }

  items.push({
    contents: <i className='far fa-comment-alt' />,
    tooltip: `View this ${QUESTION.SINGULAR}'s comments`,
    disabled: isAggregate,
    onClick: () => handleToggleComments()
  });
  return items;
}


export const canSubmitAndVerifyAction = ({ status }: Pick<UniversalTrackerValuePlain, 'status'>, action: number) => {
  const { UPDATED, REJECTED, VERIFIED } = UpdateActions.ACTION.UTR;
  switch (action) {
    case SubmitVerifiedButtonState.Show:
      return [REJECTED, UPDATED, VERIFIED].includes(status);
    case SubmitVerifiedButtonState.Enable:
      return [REJECTED, VERIFIED].includes(status);
    default:
      return false;
  }
}

export const getSubmitButtonTooltip = (
  { status }: Pick<UniversalTrackerValuePlain, 'status'>,
  isDisabled: boolean,
  isContributorAndVerifier: boolean
) => {
  const { CREATED, UPDATED, REJECTED, VERIFIED } = UpdateActions.ACTION.UTR;
  if (!isDisabled) {
    return isContributorAndVerifier ? QuestionButtonTooltip.SubmitWithoutVerification : '';
  }

  switch (status) {
    case UPDATED:
      return isContributorAndVerifier ? QuestionButtonTooltip.UpdateToSubmit : QuestionButtonTooltip.NothingToSubmit;
    case VERIFIED:
      return isContributorAndVerifier ? QuestionButtonTooltip.NothingToSubmit : QuestionButtonTooltip.UnableToSubmit;
    case CREATED:
    case REJECTED:
    default:
      return QuestionButtonTooltip.NothingToSubmit;
  }
};

export const getVerifyButtonTooltip = (isDisabled: boolean) => {
  return isDisabled ? QuestionButtonTooltip.NothingToVerify : ''
}

export const getSubmitAndVerifyButtonTooltip = (
  { verificationRequired }: Pick<UniversalTrackerValuePlain, 'verificationRequired'>,
  isDisabled: boolean,
) => {
  if (!isDisabled) {
    return verificationRequired ? QuestionButtonTooltip.SubmitAsVerification : '';
  }
  if (verificationRequired) {
    return QuestionButtonTooltip.NothingToSubmitOrVerify;
  }
  return QuestionButtonTooltip.NothingToSubmit;
};
